import type { ReactNode } from 'react';
import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import {
  Check,
  CheckCheck,
  ChevronDown,
  FileText,
  Link2,
  ListChecks,
  Lock,
  MessageCircle,
  Mic,
  Phone,
  Play,
  Plus,
  Smile,
  Star,
  Video,
} from 'lucide-react';
import { avatarPairIndex } from '../../theme';
import { useT } from '../../i18n';

// The landing page's miniature of the app (D353): the family bar, a chat
// with reactions, a photo, a voice note, a call and a list item, the
// composer and the floating app bar, drawn from plain markup and the theme.
// It is what ad visitors see first, mostly on phones, so it imports nothing
// from the app itself (no chat state, crypto, sockets or emoji data) and no
// images: the photo and the waveform are inline SVG and CSS. It is a
// picture, not a control: one label for screen readers, nothing focusable.

// The system's colour emoji fonts (as lib/emoji's EMOJI_FONT), inlined so
// the preview doesn't pull in the emoji rules.
const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

// A voice note's bars, 0–100, as the real player draws them (D221).
const WAVE = [
  30, 55, 80, 45, 65, 95, 70, 40, 60, 85, 50, 35, 70, 90, 55, 30, 45, 75, 60,
  40, 25, 50,
];

function Face({ name, size = 26 }: { name: string; size?: number }) {
  const i = avatarPairIndex(name);
  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      bg={`avatar.${i}.bg`}
      color={`avatar.${i}.fg`}
      fontSize="11px"
      fontWeight={700}
      align="center"
      justify="center"
      flexShrink={0}
    >
      {name.charAt(0)}
    </Flex>
  );
}

// Someone else's message in a group: face, name, bubble (D257).
function Theirs({
  name,
  time,
  children,
  below,
}: {
  name: string;
  time: string;
  children: ReactNode;
  below?: ReactNode;
}) {
  return (
    <Flex gap="6px" align="flex-start" maxW="88%">
      <Face name={name} />
      <Flex direction="column" align="flex-start" minW={0}>
        <Text fontSize="11px" fontWeight={700} color="muted" ms="10px" mb="1px">
          {name}
        </Text>
        <Box
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="18px"
          px="10px"
          py="5px"
          maxW="full"
        >
          {children}
          <Text
            fontSize="10px"
            color="muted"
            mt="1px"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {time}
          </Text>
        </Box>
        {below}
      </Flex>
    </Flex>
  );
}

// Reactions hang off the bubble's bottom edge (D259, D264); mine is outlined.
function Reaction({
  emoji,
  count,
  mine,
}: {
  emoji: string;
  count: number;
  mine?: boolean;
}) {
  return (
    <HStack
      data-testid="preview-reaction"
      spacing="3px"
      h="22px"
      px="6px"
      borderRadius="full"
      borderWidth="1px"
      borderColor={mine ? 'accent' : 'line'}
      bg={mine ? 'accent.tint' : 'surface'}
      boxShadow="0 1px 2px rgba(0, 0, 0, 0.08)"
    >
      <Box as="span" fontSize="13px" lineHeight="1" fontFamily={EMOJI_FONT}>
        {emoji}
      </Box>
      <Text as="span" fontSize="11px" fontWeight={600} color="ink.body">
        {count}
      </Text>
    </HStack>
  );
}

// Gulab jamun in a bowl, drawn rather than photographed (no external images).
function DessertPhoto() {
  return (
    <Box
      as="svg"
      viewBox="0 20 200 78"
      w="full"
      h="auto"
      display="block"
      borderRadius="12px"
    >
      <defs>
        <linearGradient id="tw-preview-sky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FBE7BF" />
          <stop offset="1" stopColor="#E8D3D6" />
        </linearGradient>
        <radialGradient id="tw-preview-jamun" cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#B4561E" />
          <stop offset="1" stopColor="#5E2410" />
        </radialGradient>
      </defs>
      <rect width="200" height="116" fill="url(#tw-preview-sky)" />
      <circle cx="168" cy="22" r="26" fill="#F7D68F" opacity="0.6" />
      <ellipse
        cx="100"
        cy="100"
        rx="88"
        ry="10"
        fill="#7A1F2B"
        opacity="0.12"
      />
      <path d="M34 66 Q100 128 166 66 Z" fill="#FFFFFF" />
      <ellipse cx="100" cy="66" rx="66" ry="14" fill="#F3EEE6" />
      <ellipse cx="100" cy="68" rx="58" ry="10" fill="#E9B66A" opacity="0.7" />
      {[
        [72, 58],
        [100, 52],
        [128, 58],
        [86, 66],
        [114, 66],
      ].map(([cx, cy]) => (
        <g key={`${cx}-${cy}`}>
          <circle cx={cx} cy={cy} r="13" fill="url(#tw-preview-jamun)" />
          <ellipse
            cx={cx! - 4}
            cy={cy! - 5}
            rx="4"
            ry="2.5"
            fill="#FFFFFF"
            opacity="0.55"
          />
        </g>
      ))}
      <path
        d="M40 70 Q100 118 160 70"
        fill="none"
        stroke="#D6D0C6"
        strokeWidth="1.5"
      />
    </Box>
  );
}

function VoiceNote() {
  const played = 7;
  return (
    <HStack
      spacing="6px"
      h="36px"
      pl="4px"
      pr="6px"
      w="200px"
      borderRadius="full"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      data-testid="preview-voice"
    >
      <Flex
        w="30px"
        h="30px"
        borderRadius="full"
        bg="accent.solid"
        color="accent.onSolid"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Play size={13} fill="currentColor" />
      </Flex>
      <HStack spacing="2px" h="22px" flex="1" minW={0} align="center">
        {WAVE.map((bar, i) => (
          <Box
            key={i}
            w="2px"
            h={`${Math.max(10, bar)}%`}
            borderRadius="full"
            bg={i < played ? 'accent' : 'gray.300'}
            _dark={{ bg: i < played ? 'accent' : 'gray.600' }}
            flexShrink={0}
          />
        ))}
      </HStack>
      <Text
        fontSize="11px"
        color="muted"
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        0:24
      </Text>
      <Flex
        h="20px"
        px="6px"
        borderRadius="full"
        borderWidth="1px"
        borderColor="line"
        fontSize="10px"
        fontWeight={700}
        align="center"
        flexShrink={0}
      >
        1×
      </Flex>
    </HStack>
  );
}

const roundTint = {
  w: '30px',
  h: '30px',
  borderRadius: 'full',
  bg: 'accent.tint',
  color: 'accent',
  align: 'center',
  justify: 'center',
  flexShrink: 0,
} as const;

export function ChatPreview() {
  const t = useT();
  return (
    <Box
      role="img"
      aria-label={t('site.landing.preview.label')}
      data-testid="chat-preview"
      w="full"
      maxW="380px"
      bg="canvas"
      borderWidth="1px"
      borderColor="line"
      borderRadius="32px"
      overflow="hidden"
      boxShadow="0 24px 48px -24px rgba(31, 29, 27, 0.3)"
      userSelect="none"
    >
      <Box aria-hidden="true">
        {/* The family bar: picture, name and handle (D8, D217), then calls
            and Starred, as in the app's top bar. */}
        <HStack spacing="10px" px="14px" pt="10px" pb="6px">
          <Flex
            w="34px"
            h="34px"
            borderRadius="full"
            bg="orange.500"
            color="white"
            fontWeight={800}
            fontSize="16px"
            align="center"
            justify="center"
            flexShrink={0}
          >
            {t('site.landing.preview.family').charAt(0)}
          </Flex>
          <Box flex="1" minW={0}>
            <HStack spacing="4px" minW={0}>
              <Text
                fontFamily="heading"
                fontWeight={800}
                fontSize="19px"
                letterSpacing="-0.02em"
                lineHeight={1.1}
                noOfLines={1}
              >
                {t('site.landing.preview.family')}
              </Text>
              <Box flexShrink={0}>
                <ChevronDown size={16} />
              </Box>
            </HStack>
            <Text fontSize="12px" color="muted" lineHeight={1.3} noOfLines={1}>
              @sharma-ki-haveli
            </Text>
          </Box>
          <HStack spacing="12px" color="ink.body" flexShrink={0} pe="2px">
            <Phone size={19} />
            <Star size={19} />
          </HStack>
        </HStack>

        {/* The chat header: name, faces and count, the call buttons. */}
        <HStack
          spacing="8px"
          px="14px"
          py="6px"
          bg="surface"
          borderTopWidth="1px"
          borderBottomWidth="1px"
          borderColor="line"
        >
          <Box flex="1" minW={0}>
            <Text
              fontFamily="heading"
              fontWeight={800}
              fontSize="16px"
              letterSpacing="-0.02em"
              lineHeight={1.2}
            >
              {t('site.landing.preview.group')}
            </Text>
            <HStack spacing={0} mt="2px">
              {['Nani', 'Dadi', 'Arjun', 'Meera'].map((name, i) => (
                <Box
                  key={name}
                  w="14px"
                  h="14px"
                  borderRadius="full"
                  bg={`avatar.${avatarPairIndex(name)}.bg`}
                  ml={i === 0 ? 0 : '-4px'}
                  boxShadow="0 0 0 2px var(--chakra-colors-surface)"
                />
              ))}
              <Text fontSize="11px" color="muted" pl="6px">
                {t('site.landing.preview.members', { count: 5 })}
              </Text>
            </HStack>
          </Box>
          <Flex {...roundTint} data-testid="preview-voice-call">
            <Phone size={16} />
          </Flex>
          <Flex {...roundTint} data-testid="preview-video-call">
            <Video size={18} />
          </Flex>
        </HStack>

        {/* The conversation. */}
        <VStack align="stretch" spacing="6px" px="12px" pt="6px" pb="10px">
          <HStack spacing="5px" justify="center" color="muted">
            <Lock size={11} />
            <Text fontSize="11px">{t('site.landing.preview.encrypted')}</Text>
          </HStack>

          {/* A finished call, as the timeline's call summary. */}
          <HStack
            alignSelf="center"
            spacing="8px"
            py="4px"
            px="10px"
            borderRadius="14px"
            bg="surface"
            borderWidth="1px"
            borderColor="line"
          >
            <Flex
              w="22px"
              h="22px"
              borderRadius="full"
              bg="surface.subtle"
              color="ink.body"
              align="center"
              justify="center"
            >
              <Phone size={13} />
            </Flex>
            <Box>
              <Text fontSize="12px" fontWeight={700} lineHeight="16px">
                {t('site.landing.preview.callLine')}
              </Text>
              <Text fontSize="10px" color="muted" lineHeight="14px">
                {t('site.landing.preview.callWhen')}
              </Text>
            </Box>
          </HStack>

          <Theirs
            name="Nani"
            time="10:02"
            below={
              <HStack spacing="4px" mt="-8px" ms="8px" position="relative">
                <Reaction emoji="❤️" count={3} mine />
                <Reaction emoji="😋" count={2} />
              </HStack>
            }
          >
            <Text fontSize="14px" lineHeight="18px">
              {t('site.landing.preview.nani')}
            </Text>
          </Theirs>

          {/* Mine on the right, time and receipt inside (D257, D258). */}
          <Box
            alignSelf="flex-end"
            maxW="54%"
            bg="accent.tint"
            borderRadius="18px"
            p="5px"
          >
            <DessertPhoto />
            <Box px="5px" pt="4px">
              <Text fontSize="14px" lineHeight="18px">
                {t('site.landing.preview.mine')}
              </Text>
              <HStack
                spacing="3px"
                justify="flex-end"
                fontSize="10px"
                color="muted"
              >
                <Text as="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  10:04
                </Text>
                <Box as="span" color="accent" display="inline-flex">
                  <CheckCheck size={13} />
                </Box>
              </HStack>
            </Box>
          </Box>

          <Flex gap="6px" align="flex-start">
            <Face name="Dadi" />
            <Box>
              <Text fontSize="11px" fontWeight={700} color="muted" ms="10px">
                Dadi
              </Text>
              <VoiceNote />
            </Box>
          </Flex>

          {/* A list item, shared from Lists. */}
          <Theirs name="Arjun" time="10:07">
            <Text fontSize="14px" lineHeight="18px">
              {t('site.landing.preview.arjun')}
            </Text>
            <Box
              mt="4px"
              bg="surface.subtle"
              borderRadius="12px"
              px="10px"
              py="5px"
              data-testid="preview-list"
            >
              <HStack spacing="5px" color="accent" mb="2px">
                <ListChecks size={13} />
                <Text fontSize="12px" fontWeight={700}>
                  {t('site.landing.preview.listName')}
                </Text>
              </HStack>
              <HStack spacing="6px">
                <Box
                  w="13px"
                  h="13px"
                  borderRadius="4px"
                  borderWidth="1.5px"
                  borderColor="muted"
                  flexShrink={0}
                />
                <Text fontSize="12px" color="ink.body">
                  {t('site.landing.preview.listItem')}
                </Text>
              </HStack>
              <HStack spacing="6px" mt="2px">
                <Flex
                  w="13px"
                  h="13px"
                  borderRadius="4px"
                  bg="accent.solid"
                  color="accent.onSolid"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Check size={10} strokeWidth={3} />
                </Flex>
                <Text
                  fontSize="12px"
                  color="muted"
                  textDecoration="line-through"
                >
                  {t('site.landing.preview.listDone')}
                </Text>
              </HStack>
            </Box>
          </Theirs>
        </VStack>

        {/* The composer: +, emoji, the field, the mic. */}
        <Box px="10px">
          <HStack
            spacing="4px"
            bg="surface"
            borderRadius="30px"
            p="3px"
            boxShadow="0 0 0 1px var(--chakra-colors-line), 0 8px 16px -10px rgba(31, 29, 27, 0.3)"
          >
            <Flex {...roundTint}>
              <Plus size={18} />
            </Flex>
            <Flex w="30px" justify="center" color="muted" flexShrink={0}>
              <Smile size={19} />
            </Flex>
            <Text flex="1" minW={0} fontSize="13px" color="muted" noOfLines={1}>
              {t('site.landing.preview.placeholder')}
            </Text>
            <Flex
              {...roundTint}
              bg="accent.solid"
              color="accent.onSolid"
              data-testid="preview-mic"
            >
              <Mic size={17} />
            </Flex>
          </HStack>
        </Box>

        {/* The floating app bar: Chats, Documents, Links, Lists. Starred
            is in the top bar, as in the app. */}
        <Flex justify="center" pt="8px" pb="10px">
          <HStack
            spacing="2px"
            p="4px"
            bg="nav.bg"
            borderRadius="full"
            boxShadow="0 10px 20px -8px rgba(31, 29, 27, 0.4)"
            data-testid="preview-nav"
          >
            <HStack
              spacing="5px"
              h="32px"
              px="12px"
              borderRadius="full"
              bg="saffron"
              color="#1F1D1B"
            >
              <MessageCircle size={16} />
              <Text fontSize="13px" fontWeight={700}>
                {t('site.landing.preview.navChats')}
              </Text>
            </HStack>
            {[FileText, Link2, ListChecks].map((Icon, i) => (
              <Flex
                key={i}
                w="38px"
                h="32px"
                align="center"
                justify="center"
                color="nav.fg"
              >
                <Icon size={18} />
              </Flex>
            ))}
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}
